import React, { useEffect, useState } from "react";
import { Plus, Trash2, TrendingUp, DollarSign, Users, FileText, Scale, Code2, Loader2, CheckCircle2, Clock, XCircle } from "lucide-react";
import { api } from "../../api";

/* ============================================================
   Shared table primitives for CRM department subviews
   ============================================================ */
function useDept(resource, defaults) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState(defaults);
  const [creating, setCreating] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/crm/dept/${resource}`);
      setRows(data || []);
    } catch { setRows([]); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    setCreating(true);
    try {
      const { data } = await api.post(`/crm/dept/${resource}`, form);
      setRows(r => [data, ...r]);
      setForm(defaults);
    } catch (e) { alert(e?.response?.data?.detail || "Could not create"); }
    setCreating(false);
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this record?")) return;
    await api.delete(`/crm/dept/${resource}/${id}`);
    setRows(r => r.filter(x => x.id !== id));
  };

  return { rows, loading, form, setForm, create, remove, creating };
}

function DeptShell({ title, icon: Icon, tint, children }) {
  return (
    <div className="p-5 space-y-4 bg-slate-50 min-h-full">
      <div className="flex items-center gap-3">
        <div className="w-9 h-9 rounded-md flex items-center justify-center"
             style={{ background: `linear-gradient(140deg, ${tint}, ${tint}aa)`, boxShadow: `0 4px 14px ${tint}55` }}>
          <Icon size={16} className="text-white" />
        </div>
        <h2 className="text-lg font-semibold text-slate-800">{title}</h2>
      </div>
      {children}
    </div>
  );
}

function Field({ label, value, onChange, type = "text", cls = "" }) {
  return (
    <div className={cls}>
      <label className="text-[10px] uppercase tracking-widest text-slate-500 mb-1 block mono font-semibold">{label}</label>
      <input type={type} value={value || ""} onChange={(e) => onChange(e.target.value)}
        className="w-full bg-white border border-slate-200 rounded px-3 py-2 text-sm text-slate-800 placeholder:text-slate-400 outline-none focus:border-indigo-400" />
    </div>
  );
}

function Chip({ children, color }) {
  return (
    <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded"
      style={{ background: `${color}22`, color, border: `1px solid ${color}55` }}>
      {children}
    </span>
  );
}

/* ============================================================
   1) MARKETING — Campaigns
   ============================================================ */
export function Marketing() {
  const { rows, form, setForm, create, remove } = useDept("campaigns", {
    name: "", channel: "email", budget: 0, spent: 0, leads: 0, status: "planned",
  });

  return (
    <DeptShell title="Marketing · Campaigns" icon={TrendingUp} tint="#f97316">
      <div className="grid grid-cols-1 md:grid-cols-6 gap-2 items-end p-3 rounded-lg bg-white border border-slate-200 shadow-sm">
        <Field label="Name" value={form.name} onChange={(v) => setForm(f => ({ ...f, name: v }))} cls="md:col-span-2" />
        <div>
          <label className="text-[10px] uppercase tracking-widest text-slate-500 mb-1 block mono">Channel</label>
          <select value={form.channel} onChange={(e) => setForm(f => ({ ...f, channel: e.target.value }))}
            className="w-full bg-white border border-slate-200 rounded px-2 py-2 text-sm">
            {["email","google","linkedin","twitter","instagram","event","referral"].map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <Field label="Budget" type="number" value={form.budget} onChange={(v) => setForm(f => ({ ...f, budget: v }))} />
        <Field label="Leads" type="number" value={form.leads} onChange={(v) => setForm(f => ({ ...f, leads: v }))} />
        <button data-testid="crm-mkt-add" onClick={create}
          className="text-xs font-semibold px-3 py-2 rounded bg-orange-500 hover:bg-orange-600 text-white flex items-center gap-1 justify-center">
          <Plus size={12} /> Add
        </button>
      </div>
      <SimpleTable rows={rows} onDelete={remove}
        columns={[
          { k: "name", label: "Campaign" },
          { k: "channel", label: "Channel", render: (r) => <Chip color="#f97316">{r.channel}</Chip> },
          { k: "budget", label: "Budget", render: (r) => `₹${Number(r.budget || 0).toLocaleString("en-IN")}` },
          { k: "spent", label: "Spent", render: (r) => `₹${Number(r.spent || 0).toLocaleString("en-IN")}` },
          { k: "leads", label: "Leads" },
          { k: "status", label: "Status", render: (r) => <Chip color={STATUS_COLORS[r.status] || "#94a3b8"}>{r.status}</Chip> },
        ]}
      />
    </DeptShell>
  );
}

/* ============================================================
   2) HR — Employees / Attendance
   ============================================================ */
export function HR() {
  const { rows, form, setForm, create, remove } = useDept("employees", {
    name: "", role: "", department: "engineering", email: "", status: "active", start_date: "",
  });

  return (
    <DeptShell title="HR · Employees" icon={Users} tint="#38bdf8">
      <div className="grid grid-cols-1 md:grid-cols-7 gap-2 items-end p-3 rounded-lg bg-white border border-slate-200 shadow-sm">
        <Field label="Name" value={form.name} onChange={(v) => setForm(f => ({ ...f, name: v }))} cls="md:col-span-2" />
        <Field label="Role" value={form.role} onChange={(v) => setForm(f => ({ ...f, role: v }))} />
        <div>
          <label className="text-[10px] uppercase tracking-widest text-slate-500 mb-1 block mono">Dept</label>
          <select value={form.department} onChange={(e) => setForm(f => ({ ...f, department: e.target.value }))}
            className="w-full bg-white border border-slate-200 rounded px-2 py-2 text-sm">
            {["engineering","sales","marketing","finance","legal","hr","design","support"].map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <Field label="Email" value={form.email} onChange={(v) => setForm(f => ({ ...f, email: v }))} />
        <Field label="Start Date" type="date" value={form.start_date} onChange={(v) => setForm(f => ({ ...f, start_date: v }))} />
        <button data-testid="crm-hr-add" onClick={create}
          className="text-xs font-semibold px-3 py-2 rounded bg-sky-500 hover:bg-sky-600 text-white flex items-center gap-1 justify-center">
          <Plus size={12} /> Add
        </button>
      </div>
      <SimpleTable rows={rows} onDelete={remove}
        columns={[
          { k: "name", label: "Name" },
          { k: "role", label: "Role" },
          { k: "department", label: "Dept", render: (r) => <Chip color="#38bdf8">{r.department}</Chip> },
          { k: "email", label: "Email" },
          { k: "start_date", label: "Start" },
          { k: "status", label: "Status", render: (r) => <Chip color={STATUS_COLORS[r.status] || "#34d399"}>{r.status}</Chip> },
        ]}
      />
    </DeptShell>
  );
}

/* ============================================================
   3) FINANCE — Invoices + Expenses (two panels)
   ============================================================ */
export function Finance() {
  const invoicesHook = useDept("invoices", { number: "", client: "", total: 0, status: "unpaid", issue_date: "", due_date: "" });
  const expensesHook = useDept("expenses", { title: "", category: "operations", amount: 0, date: "", vendor: "" });

  return (
    <DeptShell title="Finance · Invoices & Expenses" icon={DollarSign} tint="#34d399">
      {/* Invoices */}
      <div>
        <div className="text-xs uppercase tracking-widest text-slate-500 mb-2 mono">// Invoices</div>
        <div className="grid grid-cols-1 md:grid-cols-7 gap-2 items-end p-3 rounded-lg bg-white border border-slate-200 shadow-sm">
          <Field label="Number" value={invoicesHook.form.number} onChange={(v) => invoicesHook.setForm(f => ({ ...f, number: v }))} />
          <Field label="Client" value={invoicesHook.form.client} onChange={(v) => invoicesHook.setForm(f => ({ ...f, client: v }))} cls="md:col-span-2" />
          <Field label="Total" type="number" value={invoicesHook.form.total} onChange={(v) => invoicesHook.setForm(f => ({ ...f, total: v }))} />
          <Field label="Issue" type="date" value={invoicesHook.form.issue_date} onChange={(v) => invoicesHook.setForm(f => ({ ...f, issue_date: v }))} />
          <Field label="Due" type="date" value={invoicesHook.form.due_date} onChange={(v) => invoicesHook.setForm(f => ({ ...f, due_date: v }))} />
          <button data-testid="crm-fin-inv-add" onClick={invoicesHook.create}
            className="text-xs font-semibold px-3 py-2 rounded bg-emerald-500 hover:bg-emerald-600 text-white flex items-center gap-1 justify-center">
            <Plus size={12} /> Add
          </button>
        </div>
        <SimpleTable rows={invoicesHook.rows} onDelete={invoicesHook.remove}
          columns={[
            { k: "number", label: "Number" },
            { k: "client", label: "Client" },
            { k: "total", label: "Total", render: (r) => `₹${Number(r.total || 0).toLocaleString("en-IN")}` },
            { k: "issue_date", label: "Issued" },
            { k: "due_date", label: "Due" },
            { k: "status", label: "Status", render: (r) => <Chip color={STATUS_COLORS[r.status] || "#f59e0b"}>{r.status}</Chip> },
          ]}
        />
      </div>

      {/* Expenses */}
      <div>
        <div className="text-xs uppercase tracking-widest text-slate-500 mb-2 mono">// Expenses</div>
        <div className="grid grid-cols-1 md:grid-cols-6 gap-2 items-end p-3 rounded-lg bg-white border border-slate-200 shadow-sm">
          <Field label="Title" value={expensesHook.form.title} onChange={(v) => expensesHook.setForm(f => ({ ...f, title: v }))} cls="md:col-span-2" />
          <div>
            <label className="text-[10px] uppercase tracking-widest text-slate-500 mb-1 block mono">Category</label>
            <select value={expensesHook.form.category} onChange={(e) => expensesHook.setForm(f => ({ ...f, category: e.target.value }))}
              className="w-full bg-white border border-slate-200 rounded px-2 py-2 text-sm">
              {["operations","payroll","marketing","tools","travel","office","legal","other"].map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <Field label="Amount" type="number" value={expensesHook.form.amount} onChange={(v) => expensesHook.setForm(f => ({ ...f, amount: v }))} />
          <Field label="Vendor" value={expensesHook.form.vendor} onChange={(v) => expensesHook.setForm(f => ({ ...f, vendor: v }))} />
          <button data-testid="crm-fin-exp-add" onClick={expensesHook.create}
            className="text-xs font-semibold px-3 py-2 rounded bg-red-500 hover:bg-red-600 text-white flex items-center gap-1 justify-center">
            <Plus size={12} /> Add
          </button>
        </div>
        <SimpleTable rows={expensesHook.rows} onDelete={expensesHook.remove}
          columns={[
            { k: "title", label: "Title" },
            { k: "category", label: "Category", render: (r) => <Chip color="#ef4444">{r.category}</Chip> },
            { k: "amount", label: "Amount", render: (r) => `₹${Number(r.amount || 0).toLocaleString("en-IN")}` },
            { k: "vendor", label: "Vendor" },
            { k: "date", label: "Date" },
          ]}
        />
      </div>
    </DeptShell>
  );
}

/* ============================================================
   4) LEGAL — Contracts
   ============================================================ */
export function Legal() {
  const { rows, form, setForm, create, remove } = useDept("contracts", {
    title: "", counterparty: "", type: "NDA", value: 0, start_date: "", end_date: "", status: "draft",
  });

  return (
    <DeptShell title="Legal · Contracts & NDAs" icon={Scale} tint="#a78bfa">
      <div className="grid grid-cols-1 md:grid-cols-7 gap-2 items-end p-3 rounded-lg bg-white border border-slate-200 shadow-sm">
        <Field label="Title" value={form.title} onChange={(v) => setForm(f => ({ ...f, title: v }))} cls="md:col-span-2" />
        <Field label="Counterparty" value={form.counterparty} onChange={(v) => setForm(f => ({ ...f, counterparty: v }))} />
        <div>
          <label className="text-[10px] uppercase tracking-widest text-slate-500 mb-1 block mono">Type</label>
          <select value={form.type} onChange={(e) => setForm(f => ({ ...f, type: e.target.value }))}
            className="w-full bg-white border border-slate-200 rounded px-2 py-2 text-sm">
            {["NDA","MSA","SOW","Employment","Vendor","License","Other"].map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <Field label="Value" type="number" value={form.value} onChange={(v) => setForm(f => ({ ...f, value: v }))} />
        <Field label="Start" type="date" value={form.start_date} onChange={(v) => setForm(f => ({ ...f, start_date: v }))} />
        <button data-testid="crm-legal-add" onClick={create}
          className="text-xs font-semibold px-3 py-2 rounded bg-violet-500 hover:bg-violet-600 text-white flex items-center gap-1 justify-center">
          <Plus size={12} /> Add
        </button>
      </div>
      <SimpleTable rows={rows} onDelete={remove}
        columns={[
          { k: "title", label: "Title" },
          { k: "counterparty", label: "Counterparty" },
          { k: "type", label: "Type", render: (r) => <Chip color="#a78bfa">{r.type}</Chip> },
          { k: "value", label: "Value", render: (r) => `₹${Number(r.value || 0).toLocaleString("en-IN")}` },
          { k: "start_date", label: "Start" },
          { k: "end_date", label: "End" },
          { k: "status", label: "Status", render: (r) => <Chip color={STATUS_COLORS[r.status] || "#f0abfc"}>{r.status}</Chip> },
        ]}
      />
    </DeptShell>
  );
}

/* ============================================================
   5) DEVELOPMENT — Projects + Tickets
   ============================================================ */
export function Development() {
  const projHook = useDept("dev-projects", { name: "", lead: "", status: "active", progress: 0 });
  const ticketHook = useDept("dev-tickets", { title: "", project: "", assignee: "", priority: "medium", status: "open" });

  return (
    <DeptShell title="Development · Projects & Tickets" icon={Code2} tint="#ec4899">
      <div>
        <div className="text-xs uppercase tracking-widest text-slate-500 mb-2 mono">// Projects</div>
        <div className="grid grid-cols-1 md:grid-cols-6 gap-2 items-end p-3 rounded-lg bg-white border border-slate-200 shadow-sm">
          <Field label="Name" value={projHook.form.name} onChange={(v) => projHook.setForm(f => ({ ...f, name: v }))} cls="md:col-span-2" />
          <Field label="Lead" value={projHook.form.lead} onChange={(v) => projHook.setForm(f => ({ ...f, lead: v }))} />
          <Field label="Progress %" type="number" value={projHook.form.progress} onChange={(v) => projHook.setForm(f => ({ ...f, progress: v }))} />
          <button data-testid="crm-dev-proj-add" onClick={projHook.create}
            className="text-xs font-semibold px-3 py-2 rounded bg-pink-500 hover:bg-pink-600 text-white flex items-center gap-1 justify-center">
            <Plus size={12} /> Add
          </button>
        </div>
        <SimpleTable rows={projHook.rows} onDelete={projHook.remove}
          columns={[
            { k: "name", label: "Project" },
            { k: "lead", label: "Lead" },
            { k: "progress", label: "Progress", render: (r) => (
              <div className="w-24 h-1.5 rounded bg-slate-200 overflow-hidden">
                <div className="h-full" style={{ width: `${Math.min(100, Number(r.progress) || 0)}%`, background: "linear-gradient(90deg,#ec4899,#f97316)" }} />
              </div>
            )},
            { k: "status", label: "Status", render: (r) => <Chip color={STATUS_COLORS[r.status] || "#38bdf8"}>{r.status}</Chip> },
          ]}
        />
      </div>

      <div>
        <div className="text-xs uppercase tracking-widest text-slate-500 mb-2 mono">// Tickets / Bugs</div>
        <div className="grid grid-cols-1 md:grid-cols-6 gap-2 items-end p-3 rounded-lg bg-white border border-slate-200 shadow-sm">
          <Field label="Title" value={ticketHook.form.title} onChange={(v) => ticketHook.setForm(f => ({ ...f, title: v }))} cls="md:col-span-2" />
          <Field label="Project" value={ticketHook.form.project} onChange={(v) => ticketHook.setForm(f => ({ ...f, project: v }))} />
          <Field label="Assignee" value={ticketHook.form.assignee} onChange={(v) => ticketHook.setForm(f => ({ ...f, assignee: v }))} />
          <div>
            <label className="text-[10px] uppercase tracking-widest text-slate-500 mb-1 block mono">Priority</label>
            <select value={ticketHook.form.priority} onChange={(e) => ticketHook.setForm(f => ({ ...f, priority: e.target.value }))}
              className="w-full bg-white border border-slate-200 rounded px-2 py-2 text-sm">
              {["low","medium","high","urgent"].map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <button data-testid="crm-dev-tk-add" onClick={ticketHook.create}
            className="text-xs font-semibold px-3 py-2 rounded bg-rose-500 hover:bg-rose-600 text-white flex items-center gap-1 justify-center">
            <Plus size={12} /> Add
          </button>
        </div>
        <SimpleTable rows={ticketHook.rows} onDelete={ticketHook.remove}
          columns={[
            { k: "title", label: "Title" },
            { k: "project", label: "Project" },
            { k: "assignee", label: "Assignee" },
            { k: "priority", label: "Priority", render: (r) => <Chip color={PRI_COLORS[r.priority] || "#94a3b8"}>{r.priority}</Chip> },
            { k: "status", label: "Status", render: (r) => <Chip color={STATUS_COLORS[r.status] || "#f59e0b"}>{r.status}</Chip> },
          ]}
        />
      </div>
    </DeptShell>
  );
}

/* ============================================================
   Dashboard — Overview cards
   ============================================================ */
export function Dashboard() {
  const [data, setData] = useState(null);
  useEffect(() => { (async () => {
    try { const { data } = await api.get("/crm/counts"); setData(data); }
    catch { setData({ counts: {}, pipeline_open: 0, revenue_won: 0, invoice_total: 0, invoice_paid: 0, expense_total: 0, net_income: 0 }); }
  })(); }, []);
  if (!data) return <div className="p-8 text-slate-500 text-sm">Loading dashboard…</div>;

  const KPIs = [
    { label: "Pipeline Open",    value: `₹${data.pipeline_open.toLocaleString("en-IN")}`, tint: "#38bdf8" },
    { label: "Revenue Won",      value: `₹${data.revenue_won.toLocaleString("en-IN")}`,   tint: "#34d399" },
    { label: "Invoices Total",   value: `₹${data.invoice_total.toLocaleString("en-IN")}`, tint: "#a78bfa" },
    { label: "Invoices Paid",    value: `₹${data.invoice_paid.toLocaleString("en-IN")}`,  tint: "#34d399" },
    { label: "Expenses Total",   value: `₹${data.expense_total.toLocaleString("en-IN")}`, tint: "#ef4444" },
    { label: "Net Income",       value: `₹${data.net_income.toLocaleString("en-IN")}`,    tint: data.net_income >= 0 ? "#22c55e" : "#ef4444" },
  ];
  const COUNTS = [
    { key: "contacts",   label: "Contacts",  tint: "#f0abfc" },
    { key: "deals",      label: "Deals",     tint: "#a78bfa" },
    { key: "campaigns",  label: "Campaigns", tint: "#f97316" },
    { key: "employees",  label: "Employees", tint: "#38bdf8" },
    { key: "contracts",  label: "Contracts", tint: "#a78bfa" },
    { key: "projects",   label: "Projects",  tint: "#ec4899" },
    { key: "tickets",    label: "Tickets",   tint: "#fb7185" },
    { key: "quotations", label: "Quotations",tint: "#fbbf24" },
  ];
  return (
    <div className="p-5 space-y-5" data-testid="crm-dashboard">
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {KPIs.map((k) => (
          <div key={k.label} className="p-4 rounded-lg border border-slate-200 bg-white shadow-sm">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 mono">{k.label}</div>
            <div className="text-xl font-bold mt-2" style={{ color: k.tint }}>{k.value}</div>
          </div>
        ))}
      </div>
      <div>
        <div className="text-xs uppercase tracking-widest text-slate-500 mb-2 mono">// Records</div>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3">
          {COUNTS.map((c) => (
            <div key={c.key} className="p-3 rounded-lg border border-slate-200 bg-white shadow-sm">
              <div className="text-[10px] uppercase tracking-widest text-slate-500 mono">{c.label}</div>
              <div className="text-lg font-bold mt-1" style={{ color: c.tint }}>{data.counts[c.key] ?? 0}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ============================================================
   Shared table
   ============================================================ */
function SimpleTable({ rows, columns, onDelete }) {
  return (
    <div className="mt-3 rounded-lg border border-slate-200 overflow-hidden bg-white shadow-sm">
      <table className="w-full text-sm">
        <thead className="bg-slate-50">
          <tr>
            {columns.map(c => <th key={c.k} className="px-3 py-2 text-left text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{c.label}</th>)}
            <th />
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && (
            <tr><td colSpan={columns.length + 1} className="text-center text-slate-400 py-6 text-xs">No records yet.</td></tr>
          )}
          {rows.map(r => (
            <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50">
              {columns.map(c => <td key={c.k} className="px-3 py-2 text-slate-700">{c.render ? c.render(r) : (r[c.k] ?? "—")}</td>)}
              <td className="px-3 py-2 text-right">
                <button onClick={() => onDelete(r.id)} className="p-1 hover:bg-red-50 rounded text-red-500" data-testid={`crm-row-del-${r.id}`}>
                  <Trash2 size={12} />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const STATUS_COLORS = {
  planned: "#94a3b8", active: "#38bdf8", running: "#22c55e", paused: "#f59e0b", completed: "#34d399", archived: "#64748b",
  draft: "#94a3b8", pending: "#f59e0b", sent: "#38bdf8", signed: "#34d399", accepted: "#22c55e", declined: "#ef4444", expired: "#fb7185",
  paid: "#22c55e", unpaid: "#f59e0b", overdue: "#ef4444",
  open: "#38bdf8", in_progress: "#a78bfa", resolved: "#22c55e", closed: "#94a3b8",
};

const PRI_COLORS = { low: "#94a3b8", medium: "#f59e0b", high: "#ef4444", urgent: "#dc2626" };
